import { useEffect, useState } from 'react';
import { getDashboardCourtrooms } from '../api/controllerDashboardApis';
import type {Room} from "../pages/protected/controller/helpers/types"
import logger from '../utils/logger';
import { recordingSocket } from '../components/mediasoup/recordingSocket';

interface UseCourtroomRoomsResult {
    rooms: Room[];
    isLoading: boolean;
    error: string | null;
}

const extractRoomNo = (streamPath?: string): string | undefined => {
    if (!streamPath) return undefined;
    return /\/hls\/(\d+)_/.exec(streamPath)?.[1];
};

export function useCourtroomRooms(): UseCourtroomRoomsResult {
    const [rooms, setRooms] = useState<Room[]>([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const STREAMING_BACKEND_URL = import.meta.env.VITE_APP_STREAM_SERVER;

    const mapRooms = (courtRooms: any[]): Room[] => {
        return courtRooms.map((room: any) => {
            let status = "offline";
            if (room.streamingStatus === "live") status = "live";
            else if (room.recordingStatus) status = "rec";

            return {
                ...room,
                id: extractRoomNo(room.streamPath) ?? String(room.id),
                courtRoomNo: extractRoomNo(room.streamPath),
                cnr: room.cnr,
                status,
                title: room.cnr,
                vw: room.viewerCount ?? 0,
                dur: room.duration ?? "00:00",
                blk: "Courtroom",
                cs: `Court ${room.cnr}`,
                jg: "Hon'ble Judge",
                bg: status === "offline"
                    ? "linear-gradient(180deg,#374151,#111827)"
                    : "linear-gradient(180deg,#1f2937,#111827)",
                gl: status === "live"
                    ? "rgba(59,130,246,.35)"
                    : "rgba(107,114,128,.2)",
                hlsUrl: room.streamPath,
                offReason: status === "offline" ? "AV Maintenance" : undefined,
                initialSnapUrl: extractRoomNo(room.streamPath)
                    ? `${STREAMING_BACKEND_URL}/snapshots/${extractRoomNo(room.streamPath)}.jpg`
                    : undefined,
            };
        });
    };

    useEffect(() => {
        const fetchRooms = async () => {
            try {
                const res = await getDashboardCourtrooms();
                setRooms(mapRooms(res.data.courtRooms ?? []));
            } catch (err: any) {
                logger.error("🏛️ Courtrooms error:", err);
                setError(err?.message ?? 'Failed to load');
            } finally {
                setLoading(false);
            }
        };

        fetchRooms();

        const handleLiveCardUpdate = (data: any) => {
            setRooms(mapRooms(data.courtRooms ?? []));
        };
        recordingSocket.on("controller_live_court_hall_card", handleLiveCardUpdate);
        
        return () => {
            recordingSocket.off("controller_live_court_hall_card", handleLiveCardUpdate);
        };
    }, []);

    return { rooms, isLoading, error };
}